/**
 * ROI estimate derivations for the Analytics page.
 *
 * The page splits its numbers into two tiers:
 *   1. Actuals — counts and dollar amounts straight from the practice's own
 *      eligibility pipeline (returned by getRoiAnalytics). Never touched here.
 *   2. Estimates — actuals × the practice's assumptions (shared/roiAssumptions,
 *      tunable per practice), always rendered with an "Est." badge.
 *
 * Everything here is pure math so the page can recompute live as the
 * assumptions panel changes.
 */

import type { RoiAssumptions } from '../../shared/roiAssumptions'

export type { RoiAssumptions }
export {
  DEFAULT_ASSUMPTIONS,
  ASSUMPTION_BOUNDS,
  ASSUMPTION_KEYS,
  resolveAssumptions,
} from '../../shared/roiAssumptions'

/** Minutes a staffer spends on one manual MEDICAL verification (payer portal
 *  or phone; CAQH Index reports ~12 min per manual transaction — medical
 *  plans run past it once deductible questions start). */
export const MEDICAL_VERIFY_MINUTES = 13

/** Minutes for one manual VISION verification — VSP/EyeMed portals are
 *  quicker than medical, so vision checks claim far less time. */
export const VISION_VERIFY_MINUTES = 5

/** Typical lag, in days, between a visit and patient payment when the
 *  balance is chased through statements instead of collected up front. */
export const PATIENT_STATEMENT_LAG_DAYS = 45

/** Estimated staff hours saved by automating verifications, honoring the
 *  medical/vision time split — a vision check claims minutes, not the
 *  medical rate. */
export function hoursSaved(medicalVerifications: number, visionVerifications: number): number {
  return (medicalVerifications * MEDICAL_VERIFY_MINUTES + visionVerifications * VISION_VERIFY_MINUTES) / 60
}

/** The measured counts every estimate applies to. */
export interface RoiCounts {
  /** Identified patient-responsibility dollars (measured, corrections
   *  honored), split by the server so the per-visit cap stays live-tunable:
   *  amounts at or below the cap's lower bound arrive pre-summed… */
  identifiedBaseDollars: number
  /** …and larger ones arrive raw — each is clamped to
   *  `avgVisitResponsibilityUsd` in `deriveRoi` (a deductible-plan patient's
   *  stored amount is their full remaining deductible; one visit can only
   *  collect one visit's worth). */
  cappableAmounts: number[]
  /** Vision patients whose benefits came back unavailable, caught pre-service. */
  visionCaught: number
  /** Medical patients confirmed out-of-network pre-visit (billed blind these
   *  become full write-offs, not rework). */
  oonCaught: number
  /** Inactive/terminated medical policies caught pre-visit — fixable before
   *  the claim ever denies. */
  inactiveCaught: number
  medicalVerifications: number
  visionVerifications: number
}

/** All the page's estimated dollars, derived from measured counts + the
 *  practice's assumptions. */
export interface RoiDerived {
  /** Identified dollars after the per-visit cap — the figure the receipt's
   *  under-collection math line shows. */
  dollarsIdentifiedUsd: number
  /** Identified patient dollars that would have leaked post-visit. */
  underCollectionUsd: number
  /** Vision visits caught ineligible pre-service × VSP write-off. */
  vspWriteOffsUsd: number
  /** Out-of-network medical visits caught pre-visit × medical write-off. */
  medicalWriteOffsUsd: number
  /** The North Star: sum of the three cash sources above. */
  cashRecoveredUsd: number
  /** Fixable denials caught pre-visit × rework time × rate. */
  denialReworkUsd: number
  hoursReturned: number
  hoursReturnedUsd: number
  /** Cash recovered + time returned — the "Total value" tile. */
  totalValueUsd: number
}

export function deriveRoi(counts: RoiCounts, a: RoiAssumptions): RoiDerived {
  const dollarsIdentifiedUsd = counts.cappableAmounts.reduce(
    (sum, amt) => sum + Math.min(amt, a.avgVisitResponsibilityUsd),
    counts.identifiedBaseDollars,
  )
  const underCollectionUsd = dollarsIdentifiedUsd * a.postVisitLeakageRate
  const vspWriteOffsUsd = counts.visionCaught * a.vspWriteOffUsd
  const medicalWriteOffsUsd = counts.oonCaught * a.medicalWriteOffUsd
  const cashRecoveredUsd = underCollectionUsd + vspWriteOffsUsd + medicalWriteOffsUsd
  const denialReworkUsd = counts.inactiveCaught * (a.denialReworkMinutes / 60) * a.frontDeskHourlyUsd
  const hoursReturned = hoursSaved(counts.medicalVerifications, counts.visionVerifications)
  const hoursReturnedUsd = hoursReturned * a.frontDeskHourlyUsd
  return {
    dollarsIdentifiedUsd,
    underCollectionUsd,
    vspWriteOffsUsd,
    medicalWriteOffsUsd,
    cashRecoveredUsd,
    denialReworkUsd,
    hoursReturned,
    hoursReturnedUsd,
    totalValueUsd: cashRecoveredUsd + denialReworkUsd + hoursReturnedUsd,
  }
}
